/**
 * planBedroomStaging — pure placement for a staged bedroom.
 *
 * The kit, in the order a stager builds it: the bed dressed (shams, euros,
 * accent pillows, the turned-back duvet, a throw across the foot), a lamp and
 * a low vignette on each nightstand the room actually has, art on the wall
 * behind the headboard, a rug under the foot, an olive tree in a free corner.
 * Rules and sources: claude-docs/2026-10-26-bedroom-staging-research.md; the
 * bed-frame layout they produce is `../../bed/dressBed.js`, shared with the
 * debug page.
 *
 * Pure: no I/O, no Firestore, no React, no THREE.
 *
 * The bed is never written. Staged, it is drawn as the app's built-in bed —
 * bare, with the style pack's headboard — because the room carries
 * it in `stagedHosts` (returned here, persisted by the caller beside the
 * props; `../../stagedHosts.js`), and removing that is the whole of putting
 * the stock bed back. A bed whose
 * product the user picked is never swapped and gets no bedding.
 *
 * Nothing is forced: a room with no nightstand gets no lamp, a head wall with
 * a window over the bed gets no art, a rug that would leave the floor or meet
 * a door swing shrinks or is left out. Every such outcome is reported through
 * `onDrop`, the bathroom planner's contract.
 */

const {
  extractWallEndpoints,
} = require('../../../bathroom-analyzer/extraction');
const { objectWorldFromLocal } = require('../../../dorm-styling/plan/shared');
const { getFloorY } = require('../../../dorm-styling/dormDecorGeometry');
const { footprintFor } = require('../../../geometry/objectFootprint');
const {
  doPolygonsOverlap,
} = require('../../../bathroom-analyzer/pipeline/geometry');
const { objectsYOverlap } = require('../../../geometry/verticalOverlap');
const { stagingEntry } = require('../entry');
const { rankWallsByFreeRun, hangOnWall, wallFaceTopAt } = require('../walls');
const { projectToParam } = require('../../../dorm-styling/plan/wallSpans');
const {
  BED_SIZES,
  dressStagedBed,
  stagedArtForBed,
  dressNightstand,
  propBox,
} = require('../../bed/dressBed');
const {
  bedBaseY,
  bedLocal,
  pickStagedBed,
  bedHasPickedProduct,
  bedBoxOf,
  findHeadWall,
  findNightstands,
} = require('./bedHost');
const { placeRug, placeFloorPlant } = require('./floor');
const { placeStagedNightstand } = require('./nightstand');
const { WALL_END_MARGIN_M } = require('../constants');

const IN = 0.0254;
const SHOW_ALL = { pillows: true, throw: true };

/** Renderer options a prop needs from its record, beyond the pack's colours. */
function attributesFor(item, style) {
  const o = item.options || {};
  if (item.type === 'staging-art') {
    return {
      artMotif: o.artMotif,
      artColors: style?.art?.colors,
      artSeed: o.artSeed,
    };
  }
  // The throw is drawn AGAINST the duvet it lies on (its roll and folds), so
  // it carries the bed it was laid on, in its own frame.
  if (o.onBed) return { onBed: o.onBed };
  // A pillow carries its own shape, so the app draws the pillow the bed
  // layout spaced (`ownPillowShape`).
  if (o.pillowTuning) return { pillowTuning: o.pillowTuning };
  if (o.seed != null) return { seed: o.seed };
  return {};
}

/**
 * @param {object} room  a hydrated room (real opening geometry)
 * @param {object} style  a resolved staging pack
 * @param {object} [opts]  `{ idPrefix, generatedAt, onDrop }`, as the
 *   bathroom planner, and `addNightstands` (default true)
 * @returns {{ entries: object[], stagedHosts: object[], report: object }}
 *   `stagedHosts` is `[{ objectId, kind: 'bed', headboard }]` when the bed is
 *   to be drawn as the staged built-in one, else empty. `report` says what the room got.
 */
function planBedroomStagingDetailed(room, style = {}, opts = {}) {
  const report = {
    bed: null,
    size: null,
    nightstands: 0,
    addedNightstands: 0,
    art: 0,
    rug: null,
    floorPlant: false,
    dropped: [],
  };
  const empty = { entries: [], stagedHosts: [], report };
  if (!room) return empty;
  const drop = (type, reason) => {
    report.dropped.push({ type, reason });
    if (typeof opts.onDrop === 'function')
      opts.onDrop({ type, reason, roomId: room.id || null });
  };

  const { bed } = pickStagedBed(room);
  if (!bed) {
    drop('bed', 'no-bed-to-stage');
    return empty;
  }
  const idPrefix = opts.idPrefix || 'staging';
  const styleId = style?.id || null;
  const generatedAt = opts.generatedAt || null;
  let seq = 0;
  const entries = [];
  const make = (type, fields) => {
    const entry = stagingEntry({
      id: `${idPrefix}-${type}-${seq++}`,
      type,
      styleId,
      generatedAt,
      ...fields,
    });
    entries.push(entry);
    return entry;
  };

  const { box, size } = bedBoxOf(bed);
  const spec = BED_SIZES[size];
  const swap = !bedHasPickedProduct(bed);
  report.bed = bed.id;
  report.size = size;
  const ry = bed.rotation?.y || 0;
  const base = bedBaseY(bed);
  const styleOptions = { bedding: style?.bedding || {} };

  /** A bed-frame item to a world record. */
  const fromBedFrame = (item, host) => {
    const [x, y, z] = item.position;
    const at = objectWorldFromLocal(bed, x, z);
    const rot = item.rotation || [0, item.rotationY || 0, 0];
    return make(item.type, {
      center: { x: at.x, y: base + y, z: at.z },
      dimensions: item.dims,
      rotation: { x: rot[0], y: ry + rot[1], z: rot[2] },
      attributes: attributesFor(item, style),
      host,
    });
  };

  // ---- 1. The bed. Dressed only when it is swapped for the built-in bed:
  // the kit is sized to that bed's mattress and headboard, not a model's.
  const dressed = dressStagedBed({
    bedBox: box,
    size,
    bedding: 'staged',
    show: SHOW_ALL,
    styleOptions,
  });
  if (swap) {
    for (const item of dressed.items) {
      if (item.type !== 'bed') fromBedFrame(item, bed);
    }
  } else {
    drop('staging-duvet', 'bed-has-a-picked-product');
  }
  const stagedHosts = swap
    ? [
        {
          objectId: bed.id,
          kind: 'bed',
          headboard: style?.bedding?.headboard || null,
        },
      ]
    : [];

  const walls = room.walls || [];
  const endpoints = extractWallEndpoints(room) || [];
  const endpointsById = new Map(endpoints.map((e) => [e.id, e]));
  const headWall = findHeadWall(room, bed, endpointsById);

  // ---- 2. Nightstands. A twin or full sleeps one, so it dresses one
  // nightstand (the larger top); a bed for two dresses both. A pair stays
  // matched in its lamps and varies its low item: books and a vase on the
  // left, a small plant on the right. A side that wants a nightstand and has
  // none gets a staged one where it fits (`nightstand.js`), unless the caller
  // turns that off.
  const stands = findNightstands(room, bed);
  const area = (s) =>
    Math.abs(s.box.outer - s.box.inner) * (s.box.front - s.box.back);
  let dressedStands = spec.paired
    ? stands
    : stands.length
      ? [stands.reduce((a, b) => (area(b) > area(a) ? b : a))]
      : [];
  report.nightstands = dressedStands.length;
  report.addedNightstands = 0;
  const wantSides = spec.paired ? [-1, 1] : [1, -1];
  const missing = wantSides.filter(
    (side) => !dressedStands.some((s) => s.side === side)
  );
  const toAdd = spec.paired ? missing : dressedStands.length ? [] : missing;
  if (opts.addNightstands !== false) {
    for (const side of toAdd) {
      const placed = placeStagedNightstand(
        room,
        bed,
        side,
        headWall ? headWall.gap : null,
        entries
      );
      if (!placed) continue;
      const object = make('staging-nightstand', placed.fields);
      dressedStands = [...dressedStands, { side, object, box: placed.box }];
      report.addedNightstands += 1;
      // A single sleeper's bed takes the first side that fits.
      if (!spec.paired) break;
    }
  }
  dressedStands.sort((a, b) => a.side - b.side);
  if (!dressedStands.length) drop('staging-table-lamp', 'no-nightstand');
  const scanned = (room.objects || []).filter(
    (o) => o?.center && o?.dimensions
  );
  for (const stand of dressedStands) {
    const low =
      dressedStands.length === 2 && stand.side > 0 ? 'plant' : 'books';
    const items = dressNightstand(stand.box, stand.side, { lamp: true, low });
    if (!items.some((i) => i.type === 'staging-table-lamp'))
      drop('staging-table-lamp', 'nightstand-too-small');
    for (const item of items) {
      const entry = fromBedFrame(item, stand.object);
      // Something the scan already stands on this top (a lamp of its own,
      // a TV) keeps its place; the prop gives way.
      const f = footprintFor(
        entry.center,
        entry.dimensions.x,
        entry.dimensions.z,
        entry.rotation.y
      );
      const blocked = scanned.some(
        (o) =>
          o !== stand.object &&
          o !== bed &&
          objectsYOverlap(entry, o) &&
          doPolygonsOverlap(
            f,
            footprintFor(
              o.center,
              o.dimensions.x,
              o.dimensions.z,
              o.rotation?.y || 0
            )
          )
      );
      if (blocked) {
        entries.splice(entries.indexOf(entry), 1);
        drop(item.type, 'nightstand-top-occupied');
      }
    }
  }
  // A vase whose books gave way would stand on nothing.
  for (const vase of entries.filter((e) => e.type === 'staging-bud-vase')) {
    const host = vase.parents?.[0]?.id;
    const hasBooks = entries.some(
      (e) => e.type === 'staging-book-stack' && e.parents?.[0]?.id === host
    );
    if (!hasBooks) entries.splice(entries.indexOf(vase), 1);
  }

  // ---- 3. Art over the bed, on the wall at its head.
  if (!headWall) {
    drop('staging-art', 'bed-not-against-a-wall');
  } else {
    placeArt({
      room,
      bed,
      spec,
      dressed,
      headWall,
      walls,
      endpointsById,
      style,
      base,
      make,
      drop,
      report,
    });
  }

  // ---- 4. The rug, then the tree (the rug is flat, so the tree may stand
  // on its edge; it is placed second so it can see everything else).
  const rug = placeRug(room, bed, size, dressed.footZ);
  if (rug.fields) {
    make('staging-rug', { ...rug.fields, host: bed });
    report.rug = rug.shrunk ? 'shrunk' : rug.shifted ? 'shifted' : 'placed';
  } else {
    report.rug = 'dropped';
    drop('staging-rug', rug.reason || 'no-floor-for-a-rug');
  }
  // The walkway's head exemption reaches past the deepest nightstand.
  const beside = Math.max(
    20 * IN,
    ...dressedStands.map((s) => s.box.front + box.z / 2)
  );
  const plant = placeFloorPlant(room, bed, entries, beside);
  if (plant) {
    make('staging-floor-plant', plant);
    report.floorPlant = true;
  } else {
    drop('staging-floor-plant', 'no-free-corner');
  }

  return { entries, stagedHosts, report };
}

/**
 * The art, hung on the bed's head wall and over the bed — never beside it. A
 * window or a door above the headboard leaves no run over the bed, and art
 * off to one side of a bed reads as a mistake, so it is left out.
 */
function placeArt({
  room,
  bed,
  spec,
  dressed,
  headWall,
  walls,
  endpointsById,
  style,
  base,
  make,
  drop,
  report,
}) {
  const floorY = getFloorY(room);
  const { axes, wall } = headWall;
  const t = projectToParam(bed.center, axes);
  const span = (bed.dimensions.x * 2) / 3;
  const tops = [t - span / 2, t, t + span / 2]
    .map((p) => wallFaceTopAt(wall, axes, p))
    .filter((v) => v != null);
  const wallTop = (tops.length ? Math.min(...tops) : Infinity) - base;
  const above = Math.max(dressed.bp.hbTop, dressed.pillowTop);
  // The clear run of the head wall that the bed's centre falls in (or the
  // nearest one), less the margin `hangOnWall` keeps from its ends.
  const candidates = rankWallsByFreeRun(room, walls, endpointsById).filter(
    (c) => c.wall.id === wall.id
  );
  const run = candidates
    .map((c) => ({
      c,
      off: Math.max(0, c.span.start - t, t - c.span.end),
    }))
    .sort((a, b) => a.off - b.off)[0];
  const maxWidth = run
    ? run.c.span.end - run.c.span.start - 2 * WALL_END_MARGIN_M
    : 0;
  const art = stagedArtForBed({
    bedWidth: bed.dimensions.x,
    paired: spec.paired,
    above,
    wallTop,
    art: { artMotif: style?.art?.motif, artSeed: style?.art?.seed },
    maxWidth,
  });
  if (!art) {
    drop('staging-art', 'no-wall-above-the-headboard');
    return;
  }
  const depth = propBox('staging-art').z;
  const total = art.w * art.pieces.length + art.gap * (art.pieces.length - 1);
  // Over the bed: the group's centre within the bed's width, less the
  // group's own half — the prints may not hang past the mattress.
  const overBed = (fields) =>
    Math.abs(bedLocal(bed, fields.center).x) <=
    bed.dimensions.x / 2 - total / 2 + 1 * IN;
  const fields = hangOnWall(
    room,
    candidates,
    'staging-art',
    base - floorY + art.bottom + art.h / 2,
    (f) => f,
    new Set(),
    {
      dims: { x: total, y: art.h, z: depth },
      slideToward: [bed],
      accept: overBed,
    }
  );
  if (!fields) {
    drop('staging-art', 'no-clear-wall-over-the-bed');
    return;
  }
  const r = fields.rotation.y;
  const along = { x: Math.cos(r), z: -Math.sin(r) };
  // The art faces the room, the bed's +z; its +x may run either way across
  // the bed, so the companion order follows the bed's own left and right.
  const flip =
    along.x * Math.cos(bed.rotation?.y || 0) -
      along.z * Math.sin(bed.rotation?.y || 0) <
    0
      ? -1
      : 1;
  for (const piece of art.pieces) {
    const dx = piece.x * flip;
    make('staging-art', {
      center: {
        x: fields.center.x + along.x * dx,
        y: fields.center.y,
        z: fields.center.z + along.z * dx,
      },
      dimensions: { x: art.w, y: art.h, z: depth },
      rotation: fields.rotation,
      parentWallIds: fields.parentWallIds,
      attributes: {
        artMotif: piece.artMotif,
        artColors: style?.art?.colors,
        artSeed: piece.artSeed,
      },
    });
    report.art += 1;
  }
}

/** The props alone, the shape `planBathroomStaging` returns. */
function planBedroomStaging(room, style, opts) {
  return planBedroomStagingDetailed(room, style, opts).entries;
}

module.exports = { planBedroomStaging, planBedroomStagingDetailed };
